// Unified filter state interface for products table
export interface ProductsFilterState {
  // Pagination
  page_size: number;
  page: number;

  // Search and basic filters
  search: string;
  name: string;
  sku: string;

  // Categorical filters
  category: string | string[]; // Support both single and multiple selection
  family: string | string[] | undefined; // Support both single and multiple selection
  status: 'all' | 'active' | 'inactive';
  brand: string | string[]; // Support both single and multiple selection

  // Advanced filters
  tags: string[];
  barcode: string;

  // Price range
  minPrice: string;
  maxPrice: string;

  // Date filters
  created_at: string;
  updated_at: string;
  created_at_from: string;
  created_at_to: string;
  updated_at_from: string;
  updated_at_to: string;

  // Status and archived
  is_active: boolean | undefined;
  is_archived: boolean;

  // Optimization flags
  useFieldsOptimization?: boolean;
}

// Default filter values
export const DEFAULT_FILTER_VALUES = {
  all: 'all',
  active: 'active',
  inactive: 'inactive',
} as const;

// Filter change handler type
export type FilterChangeHandler = <K extends keyof ProductsFilterState>(
  key: K,
  value: ProductsFilterState[K]
) => void;

// Filter clear handler type
export type FilterClearHandler = () => void;

// Props for components that need filter state
export interface FilterProps {
  filters: ProductsFilterState;
  onFilterChange: FilterChangeHandler;
  onClearFilters: FilterClearHandler;
}

// Utility type for partial filters (used in hooks)
export type PartialFilters = Partial<ProductsFilterState>;

// Create default filter state
export function createDefaultFilterState(
  viewMode: 'list' | 'grid' = 'list',
  searchTerm: string = '',
  filterValues = DEFAULT_FILTER_VALUES
): ProductsFilterState {
  return {
    page_size: viewMode === 'list' ? 10 : 50,
    page: 1,
    search: searchTerm,
    name: '',
    sku: '',
    tags: [],
    category: filterValues.all,
    family: undefined,
    status: filterValues.all,
    minPrice: '',
    maxPrice: '',
    brand: [],
    barcode: '',
    created_at: '',
    updated_at: '',
    created_at_from: '',
    created_at_to: '',
    updated_at_from: '',
    updated_at_to: '',
    is_active: undefined,
    is_archived: false,
    useFieldsOptimization: true,
  };
}

// Helper function to clean filters (remove empty values)
export function cleanFilters(filters: ProductsFilterState): PartialFilters {
  const cleaned: PartialFilters = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '' && value !== undefined && value !== null) {
      if (Array.isArray(value) && value.length === 0) {
        return; // Skip empty arrays
      }
      if (value === DEFAULT_FILTER_VALUES.all) {
        return; // Skip 'all' values
      }
      // Handle arrays that contain 'all' values
      if (Array.isArray(value) && value.includes(DEFAULT_FILTER_VALUES.all)) {
        return; // Skip arrays containing 'all'
      }
      cleaned[key as keyof ProductsFilterState] = value;
    }
  });

  return cleaned;
}

// Helper function to validate filter values
export function validateFilterValue<K extends keyof ProductsFilterState>(
  key: K,
  value: ProductsFilterState[K]
): boolean {
  switch (key) {
    case 'page_size':
      return typeof value === 'number' && value > 0 && value <= 500;
    case 'page':
      return typeof value === 'number' && value > 0;
    case 'tags':
      return Array.isArray(value);
    case 'category':
      return typeof value === 'string' || Array.isArray(value);
    case 'family':
      return value === undefined || typeof value === 'string' || Array.isArray(value);
    case 'brand':
      return typeof value === 'string' || Array.isArray(value);
    case 'is_active':
      return value === undefined || typeof value === 'boolean';
    case 'is_archived':
      return typeof value === 'boolean';
    default:
      return true; // Allow all other values
  }
}

// Helper functions for multi-select operations
export function addToMultiSelectFilter<T>(
  currentValue: T | T[] | undefined,
  newValue: T,
  defaultAll = 'all'
): T[] {
  if (!currentValue || currentValue === defaultAll) {
    return [newValue];
  }

  if (Array.isArray(currentValue)) {
    return currentValue.includes(newValue) ? currentValue : [...currentValue, newValue];
  }

  return currentValue === newValue ? [currentValue] : [currentValue as T, newValue];
}

export function removeFromMultiSelectFilter<T>(
  currentValue: T | T[] | undefined,
  valueToRemove: T,
  defaultAll = 'all'
): T | T[] | undefined {
  if (!currentValue || currentValue === defaultAll) {
    return defaultAll as T;
  }

  if (Array.isArray(currentValue)) {
    const filtered = currentValue.filter(item => item !== valueToRemove);
    return filtered.length === 0 ? (defaultAll as T) : filtered;
  }

  return currentValue === valueToRemove ? (defaultAll as T) : currentValue;
}

export function isValueSelectedInMultiSelect<T>(
  currentValue: T | T[] | undefined,
  checkValue: T,
  defaultAll = 'all'
): boolean {
  if (!currentValue || currentValue === defaultAll) {
    return checkValue === defaultAll;
  }

  if (Array.isArray(currentValue)) {
    return currentValue.includes(checkValue);
  }

  return currentValue === checkValue;
}
